/**
 * 系统角色（users.role）
 * - admin：系统管理员（全权限）
 * - manager / admin_group：管理员组（按权限点）
 * - captain：队长
 * - leader：组长
 * - user：普通用户
 */

export const SYSTEM_ROLES = [
  {
    value: 'admin',
    label: '系统管理员',
    color: 'red',
    tip: 'role=admin，拥有全部后台能力'
  },
  {
    value: 'manager',
    aliases: ['admin_group'],
    label: '管理员组',
    color: 'orange',
    tip: 'role=manager，后台能力由分组权限点决定，不是系统管理员'
  },
  {
    value: 'captain',
    label: '队长',
    color: 'geekblue',
    tip: 'role=captain'
  },
  {
    value: 'leader',
    label: '组长',
    color: 'purple',
    tip: 'role=leader'
  },
  {
    value: 'user',
    label: '普通用户',
    color: 'default',
    tip: 'role=user，默认角色'
  }
];

const ROLE_BY_KEY = SYSTEM_ROLES.reduce((map, item) => {
  map[item.value] = item;
  (item.aliases || []).forEach((alias) => {
    map[alias] = item;
  });
  return map;
}, {});

/** 规范化角色存储值（admin_group → manager） */
export function normalizeSystemRole(role) {
  const key = String(role || 'user').trim().toLowerCase();
  const meta = ROLE_BY_KEY[key];
  return meta ? meta.value : 'user';
}

export function getSystemRoleMeta(role) {
  const normalized = normalizeSystemRole(role);
  return ROLE_BY_KEY[normalized] || ROLE_BY_KEY.user;
}

/** 是否系统管理员（仅 admin） */
export function isSystemAdminRole(role) {
  return normalizeSystemRole(role) === 'admin';
}

/** 是否管理员组角色（manager / 旧值 admin_group） */
export function isManagerRole(role) {
  return normalizeSystemRole(role) === 'manager';
}

/** 系统管理员或管理员组（可查看他人坐标转换历史等） */
export function isAdminOrManagerRole(role) {
  const normalized = normalizeSystemRole(role);
  return normalized === 'admin' || normalized === 'manager';
}

/** 从 JWT / localStorage 读取当前角色 */
export function getCurrentSystemRole() {
  try {
    const fromStorage = localStorage.getItem('role');
    if (fromStorage) return normalizeSystemRole(fromStorage);
    const token = localStorage.getItem('token');
    if (!token) return 'user';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return normalizeSystemRole(payload?.role);
  } catch (_) {
    return 'user';
  }
}

/**
 * 根据所属分组名推导系统角色（优先级：admin > manager > captain > leader > user）
 */
export function resolveRoleFromGroupNames(groupNames = []) {
  const names = (groupNames || []).map((n) => String(n || '').trim());
  if (names.some((n) => n === '系统管理员' || n === '管理员')) return 'admin';
  if (names.some((n) => n === '管理员组')) return 'manager';
  if (names.some((n) => n === '队长')) return 'captain';
  if (names.some((n) => n === '组长')) return 'leader';
  return 'user';
}

/**
 * 列表展示用角色：优先按分组推导，纠正「仅在管理员组却被标成 admin」的脏数据。
 * 真正在「系统管理员/管理员」组内的仍显示 admin。
 */
export function resolveDisplayRole(user = {}) {
  const groupNames = (user.groups || []).map((g) => (typeof g === 'string' ? g : g?.name));
  const fromGroups = resolveRoleFromGroupNames(groupNames);
  const fromDb = normalizeSystemRole(user.role);
  // 有分组信息时以分组为准（与用户授权同步规则一致）
  if (Array.isArray(user.groups)) return fromGroups;
  return fromDb;
}

export const SYSTEM_ROLE_SELECT_OPTIONS = SYSTEM_ROLES.map((item) => ({
  value: item.value,
  label: item.value
}));
